import { useState, useRef, useMemo } from "react";
import { Outlet, useNavigate, useLocation, matchPath } from "react-router-dom";
import { SelectionContext } from "../../../contexts/SelectionContext";
import useMobile from "../../../hooks/useMobile";
import { selectionBase } from "../../../utils/routes";

const SelectionLayout = () => {
  const isMobile = useMobile();

  const navigate = useNavigate();

  const currentPath = useLocation().pathname;
  const base = selectionBase();

  const [loading, setLoading] = useState(false);
  const [solutionResults, setSolutionResults] = useState(undefined);
  const [options, setOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState(undefined);
  // prettier-ignore
  const [currentActiveTab, setCurrentActiveTab] = useState("Default");
  const [currentSelectedPump, setCurrentSelectedPump] = useState(undefined);
  const [inputErrors, setInputErrors] = useState({
    emptyRule: [],
    valueRule: undefined,
  });

  const basicData = {
    units: "m", // еденицы измерения l - л/с, m - м3/ч
    Q: 0, // расход
    H: 0, // напор
    H0: 0, // статический напор
  };

  const [tabs, setTabs] = useState({
    Default: {
      type: "default",
      formData: basicData,
      error: {
        request: undefined,
        emptyRule: [],
        valueRule: undefined,
      },
    },
  });

  const componentData = useRef(null);

  const floatingRules = {
    Q: 2,
    H: 2,
    H0: 2,
  };

  const checkEmptyField = (field, value) => {
    const fieldToCheck = options.find((opt) => opt.name === field);

    const validFields = ["Q", "H", "H0"];

    if (
      fieldToCheck.required === "required" &&
      validFields.indexOf(fieldToCheck.name) !== -1
    ) {
      if (value === null) {
        // Если поле пустое, добавляем его в массив ошибок
        setInputErrors((prev) => {
          // Удаляем все объекты с таким же inputId, чтобы не было дублей
          const filteredEmptyRule = (prev.emptyRule || []).filter(
            (item) => item.inputId !== fieldToCheck.id
          );
          return {
            ...prev,
            emptyRule: [
              ...filteredEmptyRule,
              {
                inputId: fieldToCheck.id,
                message: "Поле не должно быть пустым",
              },
            ],
          };
        });
        return false;
      } else {
        // Если поле не пустое, убираем его из массива ошибок
        setInputErrors((prev) => ({
          ...prev,
          emptyRule: prev.emptyRule?.filter(
            (item) => item.inputId !== fieldToCheck.id
          ),
        }));
        return true;
      }
    }
    return true;
  };

  const handleInputChange = (name, value, measureId, measureValue) => {
    try {
      let newValue = value;

      if (floatingRules) {
        // Для дробных чисел с округлением
        if (newValue !== null && newValue !== "") {
          if (floatingRules.hasOwnProperty(name)) {
            const decimals = floatingRules[name];
            const strValue =
              typeof newValue === "string" ? newValue : String(newValue);

            if (decimals === 0) {
              // Для целых чисел - удаляем все точки
              newValue = strValue.replace(/\./g, "");
            } else {
              // Для дробных чисел - обрабатываем точку
              const dotIndex = strValue.indexOf(".");
              if (dotIndex !== -1) {
                const maxLength = dotIndex + 1 + decimals;
                newValue = strValue.substring(0, maxLength);
              }
            }
          }
        }
      } else {
        // Для всех других типов данных
        const fieldType = options.find((opt) => opt.name === name)?.fieldType;
        if (fieldType === "number" && value !== null && value !== "") {
          // Незавершенный ввод минуса не превращаем в NaN
          if (value === "-") newValue = value;
          else newValue = Number(value);
        }
      }

      checkEmptyField(name, newValue);

      if (typeof componentData.current === "function") {
        componentData.current((prev) => {
          const updated = {
            ...prev,
            [name]: newValue,
          };

          if (measureId && measureValue !== undefined) {
            updated[measureId] = measureValue;
          }

          return updated;
        });
      }
    } catch (error) {
      // console.error("Error changing input: ", error);
    }
  };

  const isTabActive = (pathPattern) => {
    return !!matchPath(pathPattern, currentPath);
  };

  const navigateTabsHandler = (path) => {
    if (!path || path.endsWith("undefined")) return;

    navigate(path);

    if (!!matchPath(base + "/:seriesId", path)) {
      setCurrentSelectedPump(undefined);
      setCurrentActiveTab("Default");
    }
  };

  const openResultsTabHandler = () => {
    if (tabs["Pump"] && tabs["Pump"].data?.id && tabs["Pump"].url) {
      navigateTabsHandler(
        `${base}/${currentPumpSeries}/${tabs["Pump"].data.id}`
      );
      setCurrentActiveTab("Pump");
      setCurrentSelectedPump(tabs["Pump"].data);
      setSolutionResults(tabs["Pump"].data);

      navigate(tabs["Pump"].url, { replace: true });
    } else return;
  };

  // Получение номера текущей серии из URL
  const currentPumpSeries = useMemo(() => {
    const segments = currentPath.split("/").filter(Boolean);
    return segments.length >= 2 ? segments[2] : null;
  }, [currentPath]);

  return (
    <SelectionContext.Provider
      value={{
        loading,
        setLoading,
        solutionResults,
        setSolutionResults,
        errorMessage,
        setErrorMessage,
        isMobile,
        inputErrors,
        setInputErrors,
        handleInputChange,
        checkEmptyField,
        setOptions,
        setComponentData: (fn) => (componentData.current = fn),
        navigate,
        tabs,
        setTabs,
        basicData,
        currentSelectedPump,
        setCurrentSelectedPump,
        currentActiveTab,
        floatingRules,
        setCurrentActiveTab,
      }}
    >
      <div className="selection-layout">
        <div className="selection-tabs page-width_large flex-column">
          <div className="tab-items_container flex-row">
            <div className="tab-items flex-row flex-between">
              <div
                className={`tab-item ${
                  isTabActive(base) ? "active" : ""
                }`}
                onClick={() => navigateTabsHandler(base)}
              >
                1. Выбор серии
              </div>
              <div
                className={`tab-item ${
                  isTabActive(base + "/:seriesId") ? "active" : ""
                }`}
                onClick={() =>
                  navigateTabsHandler(`${base}/${currentPumpSeries}`)
                }
              >
                2. Выбор параметров
              </div>
              <div
                className={`tab-item ${
                  isTabActive(base + "/:seriesId/:pumpId")
                    ? "active"
                    : ""
                }`}
                onClick={() => openResultsTabHandler()}
              >
                3. Результаты подбора
              </div>
            </div>
          </div>
        </div>
        <Outlet />
      </div>
    </SelectionContext.Provider>
  );
};

export default SelectionLayout;
